/**
 * Definition-only T1–T4 execution preview for protocol drafts.
 */

import { useMemo } from "react";
import type {
  ProtocolAtom,
  ProtocolConnection,
  ProtocolCriterion,
  ProtocolGate,
} from "../../api/clinicalTrialsApiTypes";
import { PROTOCOL_TIERS, toggleTierSelection } from "./ProtocolTierFilter";
import { ProtocolFlowStageCard } from "./ProtocolFlowStageCard";
import { buildProtocolFlowModel } from "./protocolFlowModel";

type ProtocolFlowPreviewProps = {
  criteria: ProtocolCriterion[];
  atoms: ProtocolAtom[];
  gates: ProtocolGate[];
  connections: ProtocolConnection[];
  selectedTiers: string[];
  onSelectedTiersChange: (tiers: string[]) => void;
  onOpenCriterionInTable: (criterionId: string) => void;
  onOpenCriterionInDag: (criterionId: string) => void;
};

/**
 * Render the protocol execution blueprint before live patient filtering exists.
 *
 * @param props - Draft entities, shared tier selection, and drill-down callbacks
 * @returns Flow preview element
 */
export function ProtocolFlowPreview({
  criteria,
  atoms,
  gates,
  connections,
  selectedTiers,
  onSelectedTiersChange,
  onOpenCriterionInTable,
  onOpenCriterionInDag,
}: ProtocolFlowPreviewProps) {
  const model = useMemo(
    () => buildProtocolFlowModel(criteria, atoms, gates, connections),
    [criteria, atoms, gates, connections],
  );
  const allSelected = selectedTiers.length === PROTOCOL_TIERS.length;

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">
            Protocol flow preview
          </h2>
          <p className="mt-1 max-w-3xl text-xs text-slate-600">
            Definition blueprint for how atoms progress through T1–T4. This is
            not a live patient funnel. One criterion can appear in multiple
            tiers when its atoms span those stages.
          </p>
        </div>
        <div className="text-xs text-slate-500">
          {model.totalAtomCount} atoms · {model.multiTierCriterionCount}{" "}
          multi-tier criteria · {model.crossTierEdges.length} explicit
          cross-tier edges
        </div>
      </div>

      <div className="mt-4 flex items-stretch gap-2 overflow-x-auto pb-2">
        <div className="flex min-w-[120px] flex-col justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3 py-4 text-center">
          <div className="text-xs font-semibold text-slate-800">
            Candidate population
          </div>
          <p className="mt-1 text-[11px] text-slate-500">
            Future patient filtering starts here
          </p>
        </div>

        {model.stages.map((stage, index) => (
          <div key={stage.tier} className="flex items-stretch gap-2">
            <div className="hidden items-center text-slate-400 sm:flex">→</div>
            <ProtocolFlowStageCard
              stage={stage}
              selected={!allSelected && selectedTiers.includes(stage.tier)}
              onSelectTier={(tier) =>
                onSelectedTiersChange(toggleTierSelection(selectedTiers, tier))
              }
              onOpenCriterion={(criterionId) => {
                onSelectedTiersChange([stage.tier]);
                onOpenCriterionInTable(criterionId);
              }}
            />
            {index === model.stages.length - 1 ? (
              <>
                <div className="hidden items-center text-slate-400 sm:flex">
                  →
                </div>
                <div className="flex min-w-[120px] flex-col justify-center rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-4 text-center">
                  <div className="text-xs font-semibold text-emerald-900">
                    Eligibility decision
                  </div>
                  <p className="mt-1 text-[11px] text-emerald-700">
                    Eligible / Ineligible / Needs information
                  </p>
                </div>
              </>
            ) : null}
          </div>
        ))}
      </div>

      <div className="mt-4 grid gap-3 lg:grid-cols-[2fr_1fr]">
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
          <div className="font-semibold text-slate-800">How to read this</div>
          <ul className="mt-2 list-disc space-y-1 pl-4">
            <li>
              Checks inside a tier run in parallel unless an explicit DEPENDS_ON
              / APPLIES_IF edge says otherwise.
            </li>
            <li>
              Hard-stop failures branch to Ineligible; unresolved checks may
              continue to later tiers.
            </li>
            <li>
              Click a tier to filter Table/DAG. Click a criterion card to open
              that criterion in the table for the selected tier.
            </li>
          </ul>
          {model.crossTierEdges.length > 0 ? (
            <div className="mt-3">
              <div className="font-semibold text-slate-800">
                Explicit cross-tier dependencies
              </div>
              <ul className="mt-1 space-y-1">
                {model.crossTierEdges.slice(0, 6).map((edge) => (
                  <li key={edge.connectionId} className="font-mono text-[11px]">
                    {edge.connectionType}: {edge.fromAtomId} ({edge.fromTier}) →{" "}
                    {edge.toAtomId} ({edge.toTier})
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="mt-3 text-[11px] text-slate-500">
              No explicit cross-tier DEPENDS_ON / APPLIES_IF edges are defined.
              Tier order is authoring metadata, not a hidden automatic route.
            </p>
          )}
        </div>

        <div className="rounded-lg border border-slate-200 p-3">
          <div className="text-xs font-semibold text-slate-800">
            Drill into selected tiers
          </div>
          <p className="mt-1 text-[11px] text-slate-500">
            Open the filtered table or authoring DAG without inventing patient
            counts.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              className="rounded border border-slate-300 px-3 py-1.5 text-xs"
              onClick={() => onOpenCriterionInTable("")}
            >
              Open table
            </button>
            <button
              type="button"
              className="rounded border border-slate-300 px-3 py-1.5 text-xs"
              onClick={() => onOpenCriterionInDag("")}
            >
              Open DAG
            </button>
            <button
              type="button"
              className="rounded border border-slate-300 px-3 py-1.5 text-xs"
              onClick={() => onSelectedTiersChange([...PROTOCOL_TIERS])}
            >
              Reset tiers
            </button>
          </div>
          {!allSelected ? (
            <p className="mt-2 text-[11px] text-sky-700">
              Active tier filter: {selectedTiers.join(", ")}
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
}
